/**
 * Helpers para conversão de timezone entre BRT (Brasil) e UTC
 * 
 * PREMISSA ARQUITETURAL:
 * - Backend armazena TUDO em UTC (timestamp with time zone)
 * - Frontend trabalha em horário LOCAL do usuário (BRT = UTC-3)
 * - Conversão acontece na BORDA (entrada/saída do backend)
 */

/**
 * Offset do Brasil em relação ao UTC (em horas)
 * BRT = UTC-3 (não consideramos horário de verão)
 */
const BRT_OFFSET_HOURS = 3;

/**
 * Converte horário local do Brasil (BRT, UTC-3) para UTC ISO string.
 * Construção explícita via Date.UTC() para independência do timezone do navegador.
 *
 * Motivo: `new Date('YYYY-MM-DDTHH:mm:00')` (sem 'Z') é interpretado como horário
 * LOCAL do navegador. Se o navegador já está em BRT, `getTime()` já retorna o
 * timestamp UTC correto — somar +3h novamente causaria dupla conversão (bug P0-02).
 * Date.UTC() trata os parâmetros como UTC puro, eliminando a ambiguidade.
 *
 * NOTA: Não trata DST. Brasil não tem horário de verão atualmente.
 * Se DST voltar, migrar para `date-fns-tz` com timezone 'America/Sao_Paulo'.
 *
 * @param {string} date - Data no formato YYYY-MM-DD (ex: "2026-05-05")
 * @param {string} time - Horário no formato HH:mm (ex: "23:59")
 * @returns {string} ISO 8601 em UTC (ex: "2026-05-06T02:59:00.000Z")
 *
 * @example
 * convertBRTtoUTC("2026-05-05", "23:59"); // "2026-05-06T02:59:00.000Z"
 * convertBRTtoUTC("2026-05-05", "08:00"); // "2026-05-05T11:00:00.000Z"
 */
export function convertBRTtoUTC(date, time) {
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  // Date.UTC trata os argumentos como UTC puro; somamos o offset para obter UTC equivalente
  return new Date(Date.UTC(y, m - 1, d, hh + BRT_OFFSET_HOURS, mm)).toISOString();
}

/**
 * Converte ISO string UTC para componentes de data/hora local BRT (UTC-3).
 * Usa getters UTC explícitos (`getUTCFullYear`, `getUTCHours`, etc.) para
 * independência do timezone do navegador.
 *
 * Motivo: `getMonth()`/`getHours()` (sem UTC) retornam em local time e quebram
 * em navegadores fora do Brasil. Ao subtrair o offset e ler os getters UTC,
 * o resultado é sempre correto independente do TZ do browser.
 *
 * NOTA: Não trata DST. Brasil não tem horário de verão atualmente.
 * Se DST voltar, migrar para `date-fns-tz` com timezone 'America/Sao_Paulo'.
 *
 * @param {string} isoString - ISO string UTC (ex: "2026-05-06T02:59:00Z")
 * @returns {{date: string, time: string}} Objeto com date (YYYY-MM-DD) e time (HH:mm)
 *
 * @example
 * convertUTCtoBRT("2026-05-06T02:59:00Z"); // { date: "2026-05-05", time: "23:59" }
 * convertUTCtoBRT("2026-05-05T11:00:00Z"); // { date: "2026-05-05", time: "08:00" }
 */
export function convertUTCtoBRT(isoString) {
  const utcDate = new Date(isoString);
  // Subtrai o offset BRT; depois lê os componentes UTC do resultado
  // (equivalente a "mover o relógio para BRT e ler como UTC")
  const brtDate = new Date(utcDate.getTime() - BRT_OFFSET_HOURS * 3600_000);

  const y  = brtDate.getUTCFullYear();
  const m  = String(brtDate.getUTCMonth() + 1).padStart(2, '0');
  const d  = String(brtDate.getUTCDate()).padStart(2, '0');
  const hh = String(brtDate.getUTCHours()).padStart(2, '0');
  const mm = String(brtDate.getUTCMinutes()).padStart(2, '0');

  return { date: `${y}-${m}-${d}`, time: `${hh}:${mm}` };
}

/**
 * Converte date + time local para Date object em BRT
 * Útil para comparações e manipulações antes de enviar ao backend
 * 
 * @param {string} date - Data no formato YYYY-MM-DD
 * @param {string} time - Horário no formato HH:mm
 * @returns {Date} Date object representando o horário local
 */
export function createLocalDateTime(date, time) {
  return new Date(`${date}T${time}:00`);
}

/**
 * Formata Date UTC para exibição em horário local BRT
 * 
 * @param {Date|string} dateOrString - Date object ou ISO string
 * @param {object} options - Opções de formatação do toLocaleTimeString
 * @returns {string} Horário formatado em BRT
 * 
 * @example
 * formatToBRT("2025-11-27T11:00:00Z");
 * // Resultado: "08:00"
 */
export function formatToBRT(dateOrString, options = { hour: '2-digit', minute: '2-digit' }) {
  const date = typeof dateOrString === 'string' ? new Date(dateOrString) : dateOrString;
  return date.toLocaleTimeString('pt-BR', {
    ...options,
    timeZone: 'America/Sao_Paulo'
  });
}
